import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import PublicIcon from "@mui/icons-material/Public";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { useTranslation } from "../../hooks/useTranslation";
import { getTimezoneDisplayLabel } from "../../utils/timezone";
const DIALOG_Z_INDEX = 1000010;
function SentenceWithEmphasis({ template, token, value, emphasisColor, }) {
    const [before, ...rest] = template.split(token);
    if (rest.length === 0)
        return _jsx(_Fragment, { children: template });
    return (_jsxs(_Fragment, { children: [before, _jsx(Box, { component: "span", sx: { fontWeight: 700, color: emphasisColor ?? "inherit" }, children: value }), rest.join(token)] }));
}
export function TimezoneChangeDialog({ pendingTimezone, detectedTimezone, onCancel, onConfirm, }) {
    const { t } = useTranslation("appointment");
    const { t: tCommon } = useTranslation("common");
    return (_jsxs(Dialog, { open: true, onClose: onCancel, maxWidth: "xs", fullWidth: true, sx: { zIndex: DIALOG_Z_INDEX }, slotProps: {
            paper: {
                sx: {
                    borderRadius: 3,
                    p: { xs: 2.5, sm: 3.5 },
                    m: 2,
                    maxWidth: 520,
                },
            },
        }, "aria-labelledby": "medos-timezone-change-title", children: [_jsx(IconButton, { onClick: onCancel, "aria-label": tCommon("close"), size: "small", sx: {
                    position: "absolute",
                    top: 12,
                    right: 12,
                    color: "text.secondary",
                }, children: _jsx(CloseIcon, { sx: { fontSize: 22 } }) }), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", sx: { pr: 4 }, children: [_jsxs(Box, { sx: {
                            position: "relative",
                            flexShrink: 0,
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                        }, children: [_jsx(PublicIcon, { sx: { fontSize: 30, color: "warning.main" } }), _jsx(Box, { sx: {
                                    position: "absolute",
                                    right: 6,
                                    bottom: 6,
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "warning.main",
                                    border: "2px solid",
                                    borderColor: "background.paper",
                                }, children: _jsx(PriorityHighIcon, { sx: { fontSize: 11, color: "#fff" } }) })] }), _jsx(Typography, { id: "medos-timezone-change-title", variant: "h6", sx: { fontSize: 24, fontWeight: 700 }, children: t("timezoneChangedTitle") })] }), _jsx(Divider, { sx: { mt: 2, mb: 2.5 } }), _jsx(Typography, { variant: "body1", sx: { fontSize: 15, color: "text.primary" }, children: _jsx(SentenceWithEmphasis, { template: t("timezoneChangedBody"), token: "{zone}", value: getTimezoneDisplayLabel(detectedTimezone) }) }), _jsxs(Stack, { direction: "row", spacing: 1.5, sx: {
                    mt: 2.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
                }, children: [_jsx(ScheduleOutlinedIcon, { sx: { fontSize: 24, color: "warning.main", flexShrink: 0 } }), _jsxs(Box, { children: [_jsx(Typography, { variant: "body2", sx: { fontSize: 14 }, children: _jsx(SentenceWithEmphasis, { template: t("timezoneChangedSlotsNotice", "Appointment times will be shown in {zone}."), token: "{zone}", value: getTimezoneDisplayLabel(pendingTimezone), emphasisColor: "warning.main" }) }), _jsx(Typography, { variant: "body2", sx: { fontSize: 14, mt: 1 }, children: t("timezoneChangedRevertHint") })] })] }), _jsxs(Stack, { direction: "row", spacing: 1.5, sx: { mt: 3 }, children: [_jsx(Button, { variant: "outlined", color: "inherit", onClick: onCancel, sx: {
                            flex: 1,
                            minHeight: 48,
                            fontSize: 16,
                            borderColor: "divider",
                            color: "text.primary",
                        }, children: tCommon("cancel") }), _jsx(Button, { variant: "contained", onClick: onConfirm, endIcon: _jsx(ArrowForwardIcon, { sx: { fontSize: 18 } }), sx: { flex: 1, minHeight: 48, fontSize: 16 }, children: t("timezoneChangedProceed") })] })] }));
}
